import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api, formatINR } from "@/lib/api";
import { useSettings } from "@/context/SettingsContext";
import SEO from "@/components/SEO";
import NotFound from "@/pages/NotFound";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { format } from "date-fns";
import WishlistButton from "@/components/WishlistButton";
import SocialShare from "@/components/SocialShare";
import RemindMeButton from "@/components/RemindMeButton";
import { DealCountdown } from "@/components/DealBadge";
import { Star, MapPin, Clock, Check, X, Calendar as CalIcon, Users, Minus, Plus, AlertCircle, ShieldCheck, ArrowRight } from "lucide-react";
import Breadcrumbs, { buildBreadcrumbSchema } from "@/components/Breadcrumbs";
import EnquiryModal from "@/components/EnquiryModal";
import { LocationMap } from "@/components/LocationMap";

function PaxCounter({ label, sub, value, onChange, min = 0 }) {
  return (
    <div className="flex items-center justify-between py-2">
      <div>
        <div className="text-sm font-medium">{label}</div>
        <div className="text-xs text-muted-foreground">{sub}</div>
      </div>
      <div className="flex items-center gap-3">
        <button
          onClick={() => onChange(Math.max(min, value - 1))}
          className="w-8 h-8 rounded-full border border-border flex items-center justify-center hover:border-teal disabled:opacity-30"
          disabled={value <= min}
          data-testid={`pax-${label.toLowerCase()}-minus`}
        >
          <Minus className="w-3.5 h-3.5" />
        </button>
        <span className="w-6 text-center font-medium" data-testid={`pax-${label.toLowerCase()}-count`}>{value}</span>
        <button
          onClick={() => onChange(value + 1)}
          className="w-8 h-8 rounded-full border border-border flex items-center justify-center hover:border-teal"
          data-testid={`pax-${label.toLowerCase()}-plus`}
        >
          <Plus className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
}

export default function ProductDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { settings } = useSettings();
  const siteName = settings.site_name || "HyderabadTourism.in";
  const siteUrl = settings.site_url || (typeof window !== "undefined" ? window.location.origin : "");
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeImg, setActiveImg] = useState(0);

  const [travelDate, setTravelDate] = useState();
  const [checkoutDate, setCheckoutDate] = useState();
  const [adults, setAdults] = useState(2);
  const [children, setChildren] = useState(0);
  const [infants, setInfants] = useState(0);
  const [enquiryOpen, setEnquiryOpen] = useState(false);
  const [availability, setAvailability] = useState(null);
  const [pickupPoints, setPickupPoints] = useState([]);

  // Check if online booking is enabled (product-level overrides global)
  const isBookingEnabled = product
    ? (product.online_booking_enabled !== undefined ? product.online_booking_enabled : settings.online_booking_enabled) || false
    : false;
  // Vehicle booking state
  const [selectedVehicle, setSelectedVehicle] = useState("");
  const [numVehicles, setNumVehicles] = useState(1);
  // Room booking state
  const [selectedRoom, setSelectedRoom] = useState("");
  const [numRooms, setNumRooms] = useState(1);
  const [extraAdults, setExtraAdults] = useState(0);
  const [extraChildren, setExtraChildren] = useState(0);

  useEffect(() => {
    setLoading(true);
    api.get(`/products/slug/${slug}`)
      .then((r) => {
        setProduct(r.data);
        // Fetch pickup points for map
        api.get(`/products/${r.data.id}/pickup-points`).then((pp) => setPickupPoints(pp.data)).catch(() => {});
      })
      .catch(() => setProduct(null))
      .finally(() => setLoading(false));
  }, [slug]);

  // State for date-based pricing
  const [datePricing, setDatePricing] = useState(null);

  // Check availability and date-based pricing when date changes
  useEffect(() => {
    if (product?.id && travelDate) {
      const dateStr = format(travelDate, "yyyy-MM-dd");
      api.get(`/products/${product.id}/availability?date=${dateStr}`)
        .then((r) => setAvailability(r.data))
        .catch(() => setAvailability(null));
      api.get(`/products/${product.id}/pricing?date=${dateStr}`)
        .then((r) => setDatePricing(r.data))
        .catch(() => setDatePricing(null));
    } else {
      setAvailability(null);
      setDatePricing(null);
    }
  }, [product?.id, travelDate]);

  // Build disabled dates function based on product availability
  const disableDate = (d) => {
    if (d < new Date(new Date().setHours(0, 0, 0, 0))) return true;
    const availDays = product?.available_days;
    if (availDays && availDays.length < 7) {
      const jsDow = d.getDay(); // 0=Sun ... 6=Sat
      if (!availDays.includes(jsDow)) return true;
    }
    const blackouts = product?.blackout_dates || [];
    if (blackouts.length > 0) {
      const iso = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
      if (blackouts.includes(iso)) return true;
    }
    return false;
  };

  // Per-product age labels (fallback to global settings)
  const adultAgeLabel = product?.adult_age_label || settings.adult_age_label || "12+ years";
  const childAgeLabel = product?.child_age_label || settings.child_age_label || "5-11 years";
  const infantAgeLabel = product?.infant_age_label || settings.infant_age_label || "0-4 years";
  const seniorAgeLabel = product?.senior_age_label || settings.senior_age_label || "60+ years";

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Skeleton className="aspect-[16/8] w-full rounded-2xl" />
      </div>
    );
  }

  if (!product) {
    return <NotFound />;
  }

  const bookingType = product.booking_type || "per_person";

  // Build effective product with date-based pricing overrides
  const ep = datePricing ? {
    ...product,
    adult_price: datePricing.adult_price ?? product.adult_price,
    child_price: datePricing.child_price ?? product.child_price,
    infant_price: datePricing.infant_price ?? product.infant_price,
    vehicle_options: datePricing.vehicle_options?.length ? datePricing.vehicle_options : product.vehicle_options,
    room_categories: datePricing.room_categories?.length ? datePricing.room_categories : product.room_categories,
  } : product;
  const dateNotValid = datePricing && !datePricing.valid;

  // Pricing calculation based on booking type
  let baseSub = 0;
  let numNights = 1;
  let priceLabel = "/ adult";

  if (bookingType === "per_vehicle") {
    const vo = (ep.vehicle_options || []).find((v) => v.type === selectedVehicle);
    baseSub = vo ? vo.price * numVehicles : ep.adult_price * numVehicles;
    priceLabel = "/ vehicle";
  } else if (bookingType === "per_room") {
    const rc = (ep.room_categories || []).find((r) => r.name === selectedRoom);
    if (travelDate && checkoutDate) {
      numNights = Math.max(1, Math.round((checkoutDate - travelDate) / (1000 * 60 * 60 * 24)));
    }
    const roomRate = rc ? rc.price_per_night : ep.adult_price;
    baseSub = roomRate * numRooms * numNights;
    baseSub += extraAdults * (rc?.extra_adult_charge || 0) * numNights;
    baseSub += extraChildren * (rc?.extra_child_charge || 0) * numNights;
    priceLabel = "/ room / night";
  } else {
    baseSub = adults * ep.adult_price + children * ep.child_price + infants * (ep.infant_price || 0);
  }

  const serviceCharge = baseSub * (product.service_charge || 0) / 100;
  const subtotal = baseSub + serviceCharge;
  const gstRate = product.gst_rate != null ? product.gst_rate : 5;
  const gst = subtotal * gstRate / 100;
  const total = subtotal + gst;

  const handleBookNow = () => {
    if (!travelDate) {
      toast.error(bookingType === "per_room" ? "Please select check-in date" : "Please select a travel date");
      return;
    }
    if (dateNotValid) {
      toast.error("This product is not available for the selected date. Please choose a different date.");
      return;
    }
    if (bookingType === "per_room" && !checkoutDate) {
      toast.error("Please select check-out date");
      return;
    }
    if (bookingType === "per_vehicle" && !selectedVehicle && (product.vehicle_options || []).length > 0) {
      toast.error("Please select a vehicle type");
      return;
    }
    if (bookingType === "per_room" && !selectedRoom && (product.room_categories || []).length > 0) {
      toast.error("Please select a room category");
      return;
    }
    const params = new URLSearchParams({
      productId: product.id,
      travelDate: format(travelDate, "yyyy-MM-dd"),
      adults: String(adults),
      children: String(children),
      infants: String(infants),
    });
    if (bookingType === "per_vehicle") {
      params.set("vehicleType", selectedVehicle);
      params.set("numVehicles", String(numVehicles));
    }
    if (bookingType === "per_room") {
      params.set("checkoutDate", format(checkoutDate, "yyyy-MM-dd"));
      params.set("roomCategory", selectedRoom);
      params.set("numRooms", String(numRooms));
      params.set("extraAdults", String(extraAdults));
      params.set("extraChildren", String(extraChildren));
    }
    navigate(`/checkout?${params.toString()}`);
  };

  const images = product.images?.length ? product.images : [product.featured_image];
  const mainImg = images[activeImg] || product.featured_image;

  const categoryToPath = { tour: "/tours", hotel: "/hotels", ticket: "/tickets", cab: "/cabs", adventure: "/adventure", international: "/international" };
  const breadcrumbItems = [
    { label: "Home", to: "/" },
    { label: product.category, to: categoryToPath[product.category] || `/search?category=${product.category}` },
    { label: product.title },
  ];

  // FAQ schema — pull from product.faqs if provided, else offer sensible defaults
  const faqSource = Array.isArray(product.faqs) && product.faqs.length ? product.faqs : [
    { q: "How do I book this experience?", a: `Choose your date, number of travellers on the right, and click Book Now. Payment is Razorpay-secured. You'll get a GST-compliant voucher on WhatsApp and email within minutes.` },
    { q: "What is the cancellation policy?", a: product.cancellation_policy || `Standard tiered cancellation applies. Cancel 72+ hours before travel for full refund minus payment gateway fee.` },
    { q: "Are there any hidden charges?", a: `No. The price shown includes GST (18%) and service charge. Optional add-ons are clearly marked.` },
    { q: "Do you provide GST invoices?", a: `Yes. A GST-compliant invoice is emailed with your voucher and is downloadable from My Bookings.` },
  ];
  const faqSchema = faqSource.length ? {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqSource.map((f) => ({
      "@type": "Question",
      "name": f.q,
      "acceptedAnswer": { "@type": "Answer", "text": f.a },
    })),
  } : null;

  return (
    <div className="bg-background pb-32 lg:pb-12" data-testid="product-detail-page">
      <SEO
        title={product.seo_title || product.title}
        description={product.seo_description || product.short_desc}
        image={product.og_image || product.featured_image}
        type="product"
        schemas={[
          {
            "@context": "https://schema.org",
            "@type": product.category === "hotel" ? "LodgingBusiness" : "TouristTrip",
            "name": product.title,
            "description": product.description,
            "image": product.images || [product.featured_image],
            "provider": {
              "@type": "TravelAgency",
              "name": siteName,
              "url": typeof window !== "undefined" ? window.location.origin : siteUrl,
            },
            "offers": {
              "@type": "Offer",
              "price": product.adult_price,
              "priceCurrency": "INR",
              "availability": product.status === "active" ? "https://schema.org/InStock" : "https://schema.org/OutOfStock",
              "url": typeof window !== "undefined" ? window.location.href : "",
              "validFrom": new Date().toISOString().split("T")[0],
            },
            "aggregateRating": product.review_count > 0 ? {
              "@type": "AggregateRating",
              "ratingValue": product.rating,
              "reviewCount": product.review_count,
              "bestRating": 5,
              "worstRating": 1,
            } : undefined,
            ...(product.destination ? {
              "touristType": ["Family", "Couple", "Solo", "Group"],
            } : {}),
          },
          buildBreadcrumbSchema(breadcrumbItems),
          faqSchema,
        ]}
      />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <Breadcrumbs items={breadcrumbItems} />

        {/* Gallery */}
        <div className="grid lg:grid-cols-[1fr_180px] gap-3 mb-6">
          <div className="aspect-[16/7] lg:aspect-[2/1] rounded-2xl overflow-hidden bg-muted max-h-[360px]">
            <img src={mainImg} alt={product.title} className="w-full h-full object-cover" />
          </div>
          <div className="hidden lg:flex flex-col gap-3 max-h-full overflow-y-auto">
            {images.slice(0, 4).map((img, i) => (
              <button
                key={i}
                onClick={() => setActiveImg(i)}
                className={`aspect-[4/3] rounded-xl overflow-hidden border-2 ${i === activeImg ? "border-saffron" : "border-transparent"}`}
                data-testid={`thumb-${i}`}
              >
                <img src={img} alt="" className="w-full h-full object-cover" />
              </button>
            ))}
          </div>
        </div>

        <div className="grid lg:grid-cols-[1fr_380px] gap-8">
          {/* Left content */}
          <div>
            <div className="flex items-center gap-2 mb-2">
              <Badge variant="outline" className="border-teal text-teal capitalize">{product.category}</Badge>
              {product.sub_category && <Badge variant="secondary" className="capitalize">{product.sub_category.toLowerCase()}</Badge>}
              <div className="flex items-center gap-1 text-sm">
                <Star className="w-4 h-4 fill-saffron text-saffron" />
                <span className="font-medium">{product.rating?.toFixed(1)}</span>
                <span className="text-muted-foreground">({product.review_count} reviews)</span>
              </div>
            </div>
            <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl mb-3" data-testid="product-title">{product.title}</h1>
            <p className="text-muted-foreground mb-4">{product.short_desc}</p>

            <DealCountdown product={product} />

            <div className="flex flex-wrap items-center gap-2 mb-5">
              <WishlistButton productId={product.id} size="sm" className="!static !bg-secondary hover:!bg-saffron/10" />
              <RemindMeButton productId={product.id} productTitle={product.title} />
              <SocialShare title={product.title} />
            </div>

            <div className="flex flex-wrap gap-4 text-sm text-muted-foreground mb-6">
              {product.destination && (
                <div className="flex items-center gap-1.5"><MapPin className="w-4 h-4 text-teal" />{product.destination}</div>
              )}
              {product.duration && (
                <div className="flex items-center gap-1.5"><Clock className="w-4 h-4 text-teal" />{product.duration}</div>
              )}
              {product.venue && (
                <div className="flex items-center gap-1.5"><MapPin className="w-4 h-4 text-teal" />{product.venue}</div>
              )}
            </div>

            <Tabs defaultValue="overview" className="mb-8">
              <TabsList className="w-full justify-start bg-secondary/50">
                <TabsTrigger value="overview" data-testid="tab-overview">Overview</TabsTrigger>
                {product.itinerary?.length > 0 && <TabsTrigger value="itinerary" data-testid="tab-itinerary">Itinerary</TabsTrigger>}
                <TabsTrigger value="inclusions" data-testid="tab-inclusions">Inclusions</TabsTrigger>
                <TabsTrigger value="policy" data-testid="tab-policy">Policy</TabsTrigger>
                {product.reviews?.length > 0 && <TabsTrigger value="reviews" data-testid="tab-reviews">Reviews</TabsTrigger>}
              </TabsList>

              <TabsContent value="overview" className="pt-6">
                <p className="text-foreground/80 leading-relaxed whitespace-pre-line">{product.description}</p>
                {product.amenities?.length > 0 && (
                  <div className="mt-6">
                    <h3 className="font-semibold mb-3">Amenities</h3>
                    <div className="flex flex-wrap gap-2">
                      {product.amenities.map((a) => (
                        <Badge key={a} variant="outline" className="border-teal/40 text-teal">{a}</Badge>
                      ))}
                    </div>
                  </div>
                )}
              </TabsContent>

              {product.itinerary?.length > 0 && (
                <TabsContent value="itinerary" className="pt-6">
                  <div className="space-y-4">
                    {product.total_days && (
                      <div className="flex items-center gap-2 mb-2">
                        <Badge variant="outline" className="text-teal border-teal">{product.total_days}D / {product.total_nights || Math.max(0, product.total_days - 1)}N</Badge>
                      </div>
                    )}
                    {product.itinerary.map((d, i) => (
                      <div key={i} className="relative pl-8 pb-4" data-testid={`itinerary-day-${i}`}>
                        {/* Timeline line */}
                        {i < product.itinerary.length - 1 && (
                          <div className="absolute left-[15px] top-8 bottom-0 w-0.5 bg-border" />
                        )}
                        {/* Day marker */}
                        <div className="absolute left-0 top-0 w-8 h-8 rounded-full bg-teal/10 border-2 border-teal flex items-center justify-center text-xs font-bold text-teal">
                          {d.day || i + 1}
                        </div>
                        <div className="bg-secondary/30 rounded-xl p-4">
                          <h4 className="font-semibold text-base mb-1">{d.title || `Day ${d.day || i + 1}`}</h4>
                          {(d.description || d.desc) && (
                            <p className="text-sm text-foreground/70 mb-3">{d.description || d.desc}</p>
                          )}
                          {/* Activities timeline */}
                          {d.activities?.length > 0 && (
                            <div className="space-y-2 mb-3">
                              {d.activities.map((act, ai) => (
                                <div key={ai} className="flex items-start gap-2 text-sm">
                                  {act.time && <span className="shrink-0 text-xs font-mono text-teal bg-teal/5 px-1.5 py-0.5 rounded">{act.time}</span>}
                                  <div>
                                    <span className="font-medium">{act.name}</span>
                                    {act.description && <p className="text-xs text-muted-foreground mt-0.5">{act.description}</p>}
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                          {/* Meals & accommodation */}
                          <div className="flex flex-wrap gap-2">
                            {d.meals?.map((m) => (
                              <Badge key={m} variant="outline" className="text-[10px]">{m}</Badge>
                            ))}
                            {d.accommodation?.name && (
                              <Badge variant="secondary" className="text-[10px]">Stay: {d.accommodation.name}</Badge>
                            )}
                            {d.transport && (
                              <Badge variant="secondary" className="text-[10px]">Transport: {d.transport}</Badge>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </TabsContent>
              )}

              <TabsContent value="inclusions" className="pt-6">
                <div className="grid sm:grid-cols-2 gap-6">
                  <div>
                    <h3 className="font-semibold mb-3 text-success flex items-center gap-2"><Check className="w-4 h-4" />Includes</h3>
                    <ul className="space-y-2">
                      {(product.includes || []).map((i, idx) => (
                        <li key={idx} className="flex gap-2 text-sm"><Check className="w-4 h-4 text-success mt-0.5 shrink-0" />{i}</li>
                      ))}
                    </ul>
                  </div>
                  <div>
                    <h3 className="font-semibold mb-3 text-destructive flex items-center gap-2"><X className="w-4 h-4" />Excludes</h3>
                    <ul className="space-y-2">
                      {(product.excludes || []).map((i, idx) => (
                        <li key={idx} className="flex gap-2 text-sm"><X className="w-4 h-4 text-destructive mt-0.5 shrink-0" />{i}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              </TabsContent>

              <TabsContent value="policy" className="pt-6">
                <div className="flex gap-3 bg-secondary/40 p-4 rounded-xl">
                  <AlertCircle className="w-5 h-5 text-saffron shrink-0 mt-0.5" />
                  <div className="text-sm leading-relaxed">{product.cancellation_policy}</div>
                </div>
                <div className="mt-4 flex gap-3 bg-secondary/40 p-4 rounded-xl">
                  <ShieldCheck className="w-5 h-5 text-teal shrink-0 mt-0.5" />
                  <div className="text-sm">Free child policy: Below 4 years travel free (no seat). 4-11 years at child price. Above 11 years at adult price.</div>
                </div>
              </TabsContent>

              {product.reviews?.length > 0 && (
                <TabsContent value="reviews" className="pt-6 space-y-4">
                  {product.reviews.map((r) => (
                    <div key={r.id} className="border border-border rounded-xl p-4">
                      <div className="flex items-center justify-between mb-2">
                        <div className="font-medium">{r.user_name}</div>
                        <div className="flex gap-0.5">
                          {[...Array(r.rating)].map((_, i) => <Star key={i} className="w-3.5 h-3.5 fill-saffron text-saffron" />)}
                        </div>
                      </div>
                      <p className="text-sm text-foreground/80">{r.comment}</p>
                    </div>
                  ))}
                </TabsContent>
              )}
            </Tabs>

            {/* Location Map */}
            {(pickupPoints.some((p) => p.lat && p.lng) || (product.venue_lat && product.venue_lng)) && (
              <div className="mb-8" data-testid="product-map-section">
                <h3 className="font-semibold text-lg mb-3 flex items-center gap-2">
                  <MapPin className="w-5 h-5 text-teal" /> Location & Pickup Points
                </h3>
                <LocationMap
                  pickupPoints={pickupPoints}
                  venueLat={product.venue_lat}
                  venueLng={product.venue_lng}
                  venueLabel={product.venue || product.title}
                />
                {pickupPoints.length > 0 && (
                  <div className="mt-3 space-y-1.5">
                    {pickupPoints.map((p) => (
                      <div key={p.id} className="flex items-center gap-2 text-sm text-muted-foreground">
                        <MapPin className="w-3.5 h-3.5 text-saffron shrink-0" />
                        <span className="font-medium text-foreground">{p.name}</span>
                        {p.time && <span>· {p.time}</span>}
                        {p.address && <span>· {p.address}</span>}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Sticky Booking Widget */}
          <aside className="hidden lg:block">
            <div className="sticky top-24 bg-white border border-border rounded-2xl p-6 shadow-sm" data-testid="booking-widget">
              {/* Price header */}
              <div className="flex items-baseline gap-2 mb-4 flex-wrap">
                <span className="text-3xl font-bold text-teal">
                  {bookingType === "per_room" && (product.room_categories || []).length > 0
                    ? formatINR((product.room_categories.find((r) => r.name === selectedRoom) || product.room_categories[0])?.price_per_night || product.adult_price)
                    : bookingType === "per_vehicle" && (product.vehicle_options || []).length > 0
                    ? formatINR((product.vehicle_options.find((v) => v.type === selectedVehicle) || product.vehicle_options[0])?.price || product.adult_price)
                    : formatINR(product.adult_price)}
                </span>
                {product.deal_active && product.deal_original_price && (
                  <span className="text-base text-muted-foreground line-through" data-testid="widget-original-price">
                    {formatINR(product.deal_original_price)}
                  </span>
                )}
                <span className="text-sm text-muted-foreground">{priceLabel}</span>
              </div>

              {/* Date picker(s) */}
              <div className="mb-4">
                <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1.5 block">
                  {bookingType === "per_room" ? "Check-in Date" : "Travel Date"}
                </label>
                <Popover>
                  <PopoverTrigger asChild>
                    <button className="w-full flex items-center gap-2 border border-border rounded-lg px-3 py-2.5 text-sm hover:border-teal" data-testid="date-picker-trigger">
                      <CalIcon className="w-4 h-4 text-teal" />
                      <span className={travelDate ? "" : "text-muted-foreground"}>
                        {travelDate ? format(travelDate, "PPP") : bookingType === "per_room" ? "Check-in date" : "Select date"}
                      </span>
                    </button>
                  </PopoverTrigger>
                  <PopoverContent className="p-0 w-auto" align="start">
                    <Calendar mode="single" selected={travelDate} onSelect={setTravelDate} disabled={disableDate} />
                  </PopoverContent>
                </Popover>
              </div>

              {bookingType === "per_room" && (
                <div className="mb-4">
                  <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1.5 block">Check-out Date</label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <button className="w-full flex items-center gap-2 border border-border rounded-lg px-3 py-2.5 text-sm hover:border-teal" data-testid="checkout-date-trigger">
                        <CalIcon className="w-4 h-4 text-teal" />
                        <span className={checkoutDate ? "" : "text-muted-foreground"}>
                          {checkoutDate ? format(checkoutDate, "PPP") : "Check-out date"}
                        </span>
                      </button>
                    </PopoverTrigger>
                    <PopoverContent className="p-0 w-auto" align="start">
                      <Calendar mode="single" selected={checkoutDate} onSelect={setCheckoutDate}
                        disabled={(d) => d <= (travelDate || new Date())} />
                    </PopoverContent>
                  </Popover>
                  {numNights > 0 && travelDate && checkoutDate && (
                    <div className="text-xs text-muted-foreground mt-1">{numNights} night{numNights > 1 ? "s" : ""}</div>
                  )}
                </div>
              )}

              {/* Availability indicator */}
              {availability && travelDate && bookingType === "per_person" && (
                <div className={`mb-3 px-3 py-2 rounded-lg text-sm flex items-center gap-2 ${
                  availability.cutoff_passed
                    ? "bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300"
                    : availability.seats_remaining !== null && availability.seats_remaining <= 5
                    ? "bg-orange-50 dark:bg-orange-900/20 text-orange-700 dark:text-orange-300"
                    : availability.available
                    ? "bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300"
                    : "bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300"
                }`} data-testid="availability-indicator">
                  <AlertCircle className="w-4 h-4 shrink-0" />
                  {availability.cutoff_passed ? (
                    <span>Booking closed — cutoff {availability.cutoff_hours}h before travel</span>
                  ) : availability.seats_remaining !== null ? (
                    availability.seats_remaining <= 0
                      ? <span>Sold out for this date</span>
                      : <span>{availability.seats_remaining} seat{availability.seats_remaining !== 1 ? "s" : ""} remaining</span>
                  ) : (
                    <span>Available</span>
                  )}
                </div>
              )}

              {/* === PER PERSON: Adults/Children/Infants === */}
              {bookingType === "per_person" && (
                <div className="border-y border-border py-3 mb-4">
                  <PaxCounter label="Adults" sub={adultAgeLabel} value={adults} onChange={setAdults} min={1} />
                  <PaxCounter label="Children" sub={childAgeLabel} value={children} onChange={setChildren} />
                  <PaxCounter label="Infants" sub={infantAgeLabel} value={infants} onChange={setInfants} />
                </div>
              )}

              {/* === PER VEHICLE: Vehicle selection === */}
              {bookingType === "per_vehicle" && (
                <div className="border-y border-border py-3 mb-4 space-y-3" data-testid="vehicle-selector">
                  <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider block">Select Vehicle</label>
                  {(product.vehicle_options || []).map((v) => (
                    <button
                      key={v.type}
                      onClick={() => setSelectedVehicle(v.type)}
                      className={`w-full flex items-center justify-between p-3 rounded-xl border-2 text-left transition-colors ${
                        selectedVehicle === v.type ? "border-teal bg-teal/5" : "border-border hover:border-teal/40"
                      }`}
                      data-testid={`vehicle-${v.type}`}
                    >
                      <div>
                        <div className="font-semibold text-sm capitalize">{v.type}</div>
                        <div className="text-xs text-muted-foreground">{v.seats} seats{v.description ? ` · ${v.description}` : ""}</div>
                      </div>
                      <div className="text-teal font-bold">{formatINR(v.price)}</div>
                    </button>
                  ))}
                  {(product.vehicle_options || []).length === 0 && (
                    <div className="text-sm text-muted-foreground">Price: {formatINR(product.adult_price)} per vehicle</div>
                  )}
                  <PaxCounter label="Vehicles" sub="Number of vehicles" value={numVehicles} onChange={setNumVehicles} min={1} />
                </div>
              )}

              {/* === PER ROOM: Room selection === */}
              {bookingType === "per_room" && (
                <div className="border-y border-border py-3 mb-4 space-y-3" data-testid="room-selector">
                  <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider block">Room Category</label>
                  {(product.room_categories || []).map((r) => (
                    <button
                      key={r.name}
                      onClick={() => setSelectedRoom(r.name)}
                      className={`w-full flex items-center justify-between p-3 rounded-xl border-2 text-left transition-colors ${
                        selectedRoom === r.name ? "border-teal bg-teal/5" : "border-border hover:border-teal/40"
                      }`}
                      data-testid={`room-${r.name}`}
                    >
                      <div>
                        <div className="font-semibold text-sm">{r.name}</div>
                        <div className="text-xs text-muted-foreground">
                          Max {r.max_adults} adult{r.max_adults !== 1 ? "s" : ""}, {r.max_children} child{r.max_children !== 1 ? "ren" : ""}
                          {r.extra_adult_charge > 0 && ` · Extra adult ${formatINR(r.extra_adult_charge)}/night`}
                        </div>
                      </div>
                      <div className="text-teal font-bold text-right">
                        <div>{formatINR(r.price_per_night)}</div>
                        <div className="text-[10px] text-muted-foreground font-normal">/night</div>
                      </div>
                    </button>
                  ))}
                  {(product.room_categories || []).length === 0 && (
                    <div className="text-sm text-muted-foreground">Price: {formatINR(product.adult_price)} per room/night</div>
                  )}
                  <PaxCounter label="Rooms" sub="Number of rooms" value={numRooms} onChange={setNumRooms} min={1} />
                  {selectedRoom && (() => {
                    const rc = (product.room_categories || []).find((r) => r.name === selectedRoom);
                    return rc?.extra_adult_charge > 0 || rc?.extra_child_charge > 0 ? (
                      <div className="space-y-1">
                        {rc.extra_adult_charge > 0 && (
                          <PaxCounter label="Extra Adults" sub={`${formatINR(rc.extra_adult_charge)}/night each`} value={extraAdults} onChange={setExtraAdults} />
                        )}
                        {rc.extra_child_charge > 0 && (
                          <PaxCounter label="Extra Children" sub={`${formatINR(rc.extra_child_charge)}/night each`} value={extraChildren} onChange={setExtraChildren} />
                        )}
                      </div>
                    ) : null;
                  })()}
                  {product.check_in_time && (
                    <div className="flex items-center gap-4 text-xs text-muted-foreground pt-1">
                      <span>Check-in: <strong>{product.check_in_time}</strong></span>
                      <span>Check-out: <strong>{product.check_out_time || "11:00 AM"}</strong></span>
                    </div>
                  )}
                </div>
              )}

              {/* Price breakdown */}
              <div className="space-y-1.5 text-sm mb-4">
                {datePricing?.label && (
                  <div className="bg-amber-50 border border-amber-200 rounded-lg px-3 py-1.5 text-xs font-semibold text-amber-700 mb-2" data-testid="surge-price-label">
                    {datePricing.label} pricing active
                  </div>
                )}
                {dateNotValid && (
                  <div className="bg-red-50 border border-red-200 rounded-lg px-3 py-2 text-xs text-red-700 mb-2" data-testid="date-not-valid">
                    {datePricing?.label || "Not available for selected date."} Use Enquire below to request availability.
                  </div>
                )}
                {total === 0 && !dateNotValid && (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg px-3 py-2 text-xs text-blue-700 mb-2" data-testid="price-not-set">
                    Rate not listed for this product. Use <strong>Enquire / Get Quote</strong> to check pricing and availability.
                  </div>
                )}
                {bookingType === "per_person" && (
                  <div className="flex justify-between"><span className="text-muted-foreground">Base ({adults}A + {children}C)</span><span>{formatINR(baseSub)}</span></div>
                )}
                {bookingType === "per_vehicle" && (
                  <div className="flex justify-between"><span className="text-muted-foreground">{numVehicles} × {selectedVehicle || "vehicle"}</span><span>{formatINR(baseSub)}</span></div>
                )}
                {bookingType === "per_room" && (
                  <div className="flex justify-between"><span className="text-muted-foreground">{numRooms} room × {numNights} night{numNights > 1 ? "s" : ""}</span><span>{formatINR(baseSub)}</span></div>
                )}
                {serviceCharge > 0 && <div className="flex justify-between"><span className="text-muted-foreground">Service charge</span><span>{formatINR(serviceCharge)}</span></div>}
                {gst > 0 && <div className="flex justify-between"><span className="text-muted-foreground">GST ({ep.gst_rate != null ? ep.gst_rate : product.gst_rate}%)</span><span>{formatINR(gst)}</span></div>}
                <div className="border-t border-border pt-2 mt-2 flex justify-between font-bold text-base">
                  <span>Total</span><span className="text-teal" data-testid="booking-total">{formatINR(total)}</span>
                </div>
              </div>

              {isBookingEnabled && total > 0 && (
                <Button onClick={handleBookNow} disabled={dateNotValid} className="w-full bg-saffron hover:bg-saffron-dark text-white text-base font-semibold py-6 rounded-xl disabled:opacity-50" data-testid="book-now-button">
                  Book Now <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              )}
              <Button
                variant={(isBookingEnabled && total > 0) ? "outline" : "default"}
                onClick={() => setEnquiryOpen(true)}
                className={`w-full ${(isBookingEnabled && total > 0) ? "mt-2 border-2" : "bg-saffron hover:bg-saffron-dark text-white"} text-base font-semibold py-6 rounded-xl`}
                data-testid="enquire-button"
              >
                {(total === 0 || dateNotValid) ? "Request Quote / Enquire" : "Enquire / Get Quote"}
              </Button>

              <div className="mt-4 text-xs text-muted-foreground flex items-center gap-1.5 justify-center">
                <ShieldCheck className="w-3.5 h-3.5 text-teal" />
                Secure checkout • Razorpay protected
              </div>
            </div>
          </aside>
        </div>
      </div>

      {/* Mobile sticky bottom bar */}
      <div className="lg:hidden fixed bottom-0 left-0 right-0 z-40 bg-white border-t border-border p-3 shadow-xl" data-testid="mobile-booking-bar">
        <div className="grid grid-cols-2 gap-2 mb-2">
          <Popover>
            <PopoverTrigger asChild>
              <button className="flex items-center gap-2 border border-border rounded-lg px-3 py-2.5 text-xs text-left hover:border-teal" data-testid="mobile-date-trigger">
                <CalIcon className="w-4 h-4 text-teal shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="text-[10px] uppercase tracking-wider text-muted-foreground leading-none">Travel</div>
                  <div className="text-sm font-semibold leading-tight truncate">{travelDate ? format(travelDate, "d MMM yyyy") : "Pick date"}</div>
                </div>
              </button>
            </PopoverTrigger>
            <PopoverContent className="p-0 w-auto" align="start">
              <Calendar mode="single" selected={travelDate} onSelect={setTravelDate} disabled={(d) => d < new Date(new Date().setHours(0, 0, 0, 0))} />
            </PopoverContent>
          </Popover>

          <Popover>
            <PopoverTrigger asChild>
              <button className="flex items-center gap-2 border border-border rounded-lg px-3 py-2.5 text-xs text-left hover:border-teal" data-testid="mobile-pax-trigger">
                <Users className="w-4 h-4 text-teal shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="text-[10px] uppercase tracking-wider text-muted-foreground leading-none">Travellers</div>
                  <div className="text-sm font-semibold leading-tight truncate">
                    {adults}A{children > 0 && ` · ${children}C`}{infants > 0 && ` · ${infants}I`}
                  </div>
                </div>
              </button>
            </PopoverTrigger>
            <PopoverContent className="w-72 p-4" align="end">
              <div className="space-y-1">
                <PaxCounter label="Adults" sub={adultAgeLabel} value={adults} onChange={setAdults} min={1} />
                <PaxCounter label="Children" sub="4-11 years" value={children} onChange={setChildren} />
                <PaxCounter label="Infants" sub="Below 4 (free)" value={infants} onChange={setInfants} />
              </div>
            </PopoverContent>
          </Popover>
        </div>

        <div className="flex items-center gap-3">
          <div className="flex-1">
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Total</div>
            <div className="flex items-baseline gap-1.5">
              <span className="text-xl font-bold text-teal">{formatINR(total)}</span>
              {product.deal_active && product.deal_original_price && (
                <span className="text-xs text-muted-foreground line-through">{formatINR(product.deal_original_price * adults + product.child_price * children)}</span>
              )}
            </div>
          </div>
          {isBookingEnabled && total > 0 && (
            <Button onClick={handleBookNow} className="bg-saffron hover:bg-saffron-dark text-white flex-1 py-6 text-base font-semibold rounded-xl" data-testid="mobile-book-button">
              Book Now
            </Button>
          )}
          <Button
            variant={(isBookingEnabled && total > 0) ? "outline" : "default"}
            onClick={() => setEnquiryOpen(true)}
            className={`py-6 rounded-xl font-semibold ${(isBookingEnabled && total > 0) ? "border-2" : "bg-saffron hover:bg-saffron-dark text-white flex-1"}`}
            data-testid="mobile-enquire-button"
          >
            {(total === 0 || dateNotValid) ? "Request Quote" : "Enquire"}
          </Button>
        </div>
      </div>

      <EnquiryModal product={product} open={enquiryOpen} onOpenChange={setEnquiryOpen} />
    </div>
  );
}
